import {
    Box,
    Typography,
    Button,
    Menu,
    MenuItem,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    DialogContentText,
    FormControl,
    InputLabel,
    Select,
    Alert
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useMemo } from "react";
import { useTheme } from "@mui/material";


import moment from "moment";
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { 
    useGetAllShiftsQuery, 
    useGetEmployeesQuery, 
    useGetAllClockingsQuery,
    useGetAvailabilitiesByEmployeeQuery,
    useGetTimeOffRequestsByEmployeeQuery,
    useCreateShiftMutation, 
    useUpdateShiftMutation, 
    useDeleteShiftMutation 
} from "../../state/api";

const Shifts = () => {
    const theme = useTheme();
    const isMobileScreen = useMediaQuery('(max-width:768px)');

    // Get current user ID from Redux state

    
    const { data: shiftsResponse, isLoading: isLoadingShifts, isError: isErrorShifts, refetch: refetchShifts } = useGetAllShiftsQuery();
    const { data: employeesResponse, isLoading: isLoadingEmployees, isError: isErrorEmployees } = useGetEmployeesQuery();
    const { data: clockingsResponse, isLoading: isLoadingClockings } = useGetAllClockingsQuery();
    const [createShift] = useCreateShiftMutation();
    const [updateShift] = useUpdateShiftMutation();
    const [deleteShift] = useDeleteShiftMutation();
    
    const shiftsData = shiftsResponse?.data;
    const employeesData = employeesResponse?.data;
    const clockingsData = Array.isArray(clockingsResponse) ? clockingsResponse : (clockingsResponse?.data || []);
    
    const [selectedFilter, setSelectedFilter] = useState("Today");
    const filterOptions = ["Today", "This Week", "Upcoming", "All"];
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [editableShiftData, setEditableShiftData] = useState(null);
    const [validationAlert, setValidationAlert] = useState({ show: false, message: '', severity: 'warning' });
    
    const [newShiftData, setNewShiftData] = useState({
        employeeId: "",
        name: "",
        date: moment().format("YYYY-MM-DD"),
        startTime: "09:00",
        endTime: "17:00",
        breakDuration: 30,
    });

    // Get availability and time-off data for selected employee
    const { data: availabilityData } = useGetAvailabilitiesByEmployeeQuery(
        newShiftData.employeeId || editableShiftData?.employeeId, 
        { skip: !newShiftData.employeeId && !editableShiftData?.employeeId }
    );
    const { data: timeOffData } = useGetTimeOffRequestsByEmployeeQuery(
        newShiftData.employeeId || editableShiftData?.employeeId, 
        { skip: !newShiftData.employeeId && !editableShiftData?.employeeId }
    );



    const handleMenuClick = (event, params) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(params.row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleViewOpen = () => {
        setOpenViewDialog(true);
        handleMenuClose();
    }

    const handleEditOpen = () => {
        const employee = employeesData?.find(emp => emp._id === selectedRow.employeeId);
        setEditableShiftData({
            ...selectedRow,
            date: moment(selectedRow.date).format("YYYY-MM-DD"),
            startTime: moment(selectedRow.startTime).format("HH:mm"),
            endTime: moment(selectedRow.endTime).format("HH:mm"),
            employeeName: employee ? `${employee.names.firstName} ${employee.names.lastName}` : 'Unknown'
        });
        setOpenEditDialog(true);
        handleMenuClose();
    };

    const handleEditSubmit = async () => {
        if (editableShiftData) {
            // Perform validation checks
            const validationResult = await performValidationChecks(editableShiftData, true);
            if (!validationResult.canProceed) {
                setValidationAlert({ show: true, message: validationResult.message, severity: validationResult.severity });
                return;
            }

            const shiftStart = moment(`${editableShiftData.date} ${editableShiftData.startTime}`);
            const shiftEnd = moment(`${editableShiftData.date} ${editableShiftData.endTime}`);
            
            const updateData = {
                ...editableShiftData,
                startTime: shiftStart.toISOString(),
                endTime: shiftEnd.toISOString(),
            };

            await updateShift({ id: editableShiftData._id, ...updateData });
            setOpenEditDialog(false);
            setValidationAlert({ show: false, message: '', severity: 'warning' });
            refetchShifts();
        }
    };

    const handleAddDialogOpen = () => {
        setOpenAddDialog(true);
        setValidationAlert({ show: false, message: '', severity: 'warning' });
    };
    
    const handleAddDialogClose = () => {
        setOpenAddDialog(false);
        setValidationAlert({ show: false, message: '', severity: 'warning' });
        setNewShiftData({
            employeeId: "",
            name: "",
            date: moment().format("YYYY-MM-DD"),
            startTime: "09:00",
            endTime: "17:00",
            breakDuration: 30,
        });
    };

    const handleNewShiftInputChange = (e) => {
        const { name, value } = e.target;
        setNewShiftData(prev => ({ ...prev, [name]: value }));
        
        // Update shift name when employee changes
        if (name === 'employeeId') {
            const employee = employeesData?.find(emp => emp._id === value);
            if (employee) {
                setNewShiftData(prev => ({ 
                    ...prev, 
                    [name]: value,
                    name: `${employee.names.firstName} ${employee.names.lastName}'s shift`
                }));
            }
        }
    };
    
    const performValidationChecks = async (shiftData, isEdit = false) => {
        const shiftStart = moment(`${shiftData.date} ${shiftData.startTime}`);
        const shiftEnd = moment(`${shiftData.date} ${shiftData.endTime}`);
        const shiftDay = shiftStart.format('dddd');

        // 1. Check for Approved Time Off
        if (timeOffData && timeOffData.length > 0) {
            const conflictingTimeOff = timeOffData.find(request => 
                request.status === 'Approved' &&
                (
                    (shiftStart.isBetween(moment(request.startDate), moment(request.endDate), null, '[]')) ||
                    (shiftEnd.isBetween(moment(request.startDate), moment(request.endDate), null, '[]')) ||
                    (moment(request.startDate).isBetween(shiftStart, shiftEnd, null, '[]'))
                )
            );

            if (conflictingTimeOff) {
                return {
                    canProceed: false,
                    message: `Employee has approved ${conflictingTimeOff.requestType} from ${moment(conflictingTimeOff.startDate).format('DD/MM/YYYY')} to ${moment(conflictingTimeOff.endDate).format('DD/MM/YYYY')}`,
                    severity: 'error'
                };
            }
        }

        // 2. Check Weekly Availability
        if (availabilityData && availabilityData.length > 0) {
            const availability = availabilityData[0]; // Assuming one availability record per employee
            const dayPattern = availability.weeklyPattern?.find(pattern => pattern.dayOfWeek === shiftDay);
            
                    if (!dayPattern || !dayPattern.isAvailable) {
          return {
            canProceed: false,
            message: `Employee is not available on ${shiftDay}. Please check their availability schedule.`,
            severity: 'warning'
          };
        } else if (dayPattern.startTime && dayPattern.endTime) {
                const availStart = moment(dayPattern.startTime, 'HH:mm');
                const availEnd = moment(dayPattern.endTime, 'HH:mm');
                const shiftStartTime = moment(shiftData.startTime, 'HH:mm');
                const shiftEndTime = moment(shiftData.endTime, 'HH:mm');
                
                if (shiftStartTime.isBefore(availStart) || shiftEndTime.isAfter(availEnd)) {
                    return {
                        canProceed: false,
                        message: `Shift time (${shiftData.startTime}-${shiftData.endTime}) is outside employee's availability (${dayPattern.startTime}-${dayPattern.endTime}). Please adjust the shift time.`,
                        severity: 'warning'
                    };
                }
            }
        }

        // 3. Check for Conflicting Shifts
        if (shiftsData && shiftsData.length > 0) {
            const conflictingShift = shiftsData.find(shift => 
                shift.employeeId === shiftData.employeeId &&
                (!isEdit || shift._id !== shiftData._id) &&
                (
                    (shiftStart.isBetween(moment(shift.startTime), moment(shift.endTime), null, '()')) ||
                    (shiftEnd.isBetween(moment(shift.startTime), moment(shift.endTime), null, '()')) ||
                    (moment(shift.startTime).isBetween(shiftStart, shiftEnd, null, '()'))
                )
            );

            if (conflictingShift) {
                return {
                    canProceed: false,
                    message: `Employee already has a conflicting shift on ${moment(conflictingShift.date).format('DD/MM/YYYY')} from ${moment(conflictingShift.startTime).format('HH:mm')} to ${moment(conflictingShift.endTime).format('HH:mm')}`,
                    severity: 'error'
                };
            }
        }

        return { canProceed: true };
    };
    
    const handleAddSubmit = async () => {
        if (!newShiftData.employeeId || !newShiftData.date || !newShiftData.startTime || !newShiftData.endTime) {
            setValidationAlert({ show: true, message: "Please fill in all required fields.", severity: 'error' });
            return;
        }
        
        try {
            // Perform validation checks
            const validationResult = await performValidationChecks(newShiftData);
            if (!validationResult.canProceed) {
                setValidationAlert({ show: true, message: validationResult.message, severity: validationResult.severity });
                return;
            }

            const shiftStart = moment(`${newShiftData.date} ${newShiftData.startTime}`);
            const shiftEnd = moment(`${newShiftData.date} ${newShiftData.endTime}`);
            
            const shiftToCreate = {
                ...newShiftData,
                startTime: shiftStart.toISOString(),
                endTime: shiftEnd.toISOString(),
            };

            await createShift(shiftToCreate).unwrap();
            handleAddDialogClose();
            refetchShifts();
        } catch (error) {
    
            setValidationAlert({ show: true, message: `Failed to create shift: ${error.data?.message || error.message}`, severity: 'error' });
        }
    };
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditableShiftData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDeleteOpen = () => {
        setOpenDeleteDialog(true);
        handleMenuClose();
    };

    const handleDelete = async () => {
        if (selectedRow) {
            await deleteShift(selectedRow._id);
            setOpenDeleteDialog(false);
            refetchShifts();
        }
    };

    const processedData = useMemo(() => {
        if (!shiftsData || !employeesData || !clockingsData || isLoadingShifts || isLoadingEmployees || isLoadingClockings) {
            return [];
        }

        if (!shiftsData.length) return [];

        return shiftsData.map(shift => {
            // Handle both populated and non-populated employeeId
            let employee = null;
            let employeeName = 'Unknown Employee';
            
            if (shift.employeeId) {
                if (typeof shift.employeeId === 'object' && shift.employeeId.names) {
                    // employeeId is populated with employee data
                    employee = shift.employeeId;
                    employeeName = `${employee.names.firstName} ${employee.names.lastName}`;
                } else {
                    // employeeId is just an ID string, find employee in employeesData
                    employee = employeesData.find(emp => emp._id === shift.employeeId);
                    employeeName = employee ? `${employee.names.firstName} ${employee.names.lastName}` : 'Unknown Employee';
                }
            }
            
            const shiftDate = moment(shift.date);
            const shiftStart = moment(shift.startTime);
            const shiftEnd = moment(shift.endTime);
            const now = moment();
            
            // Find clocking record for this shift
            const clocking = clockingsData.find(c => 
                c.shiftId === shift._id || 
                (c.employeeId === shift.employeeId && 
                 moment(c.clockInTime).isSame(shiftDate, 'day'))
            );
            
            let status = "Scheduled";
            
            // Determine status based on shift timing and clocking
            if (shiftEnd.isBefore(now)) {
                // Shift has ended
                if (clocking) {
                    status = "Completed";
                } else {
                    status = "Absent";
                }
            } else if (shiftStart.isSameOrBefore(now) && shiftEnd.isAfter(now)) {
                // Shift is currently ongoing
                if (clocking && clocking.clockInTime) {
                    status = "In Progress";
                } else {
                    status = "Waiting";
                }
            } else if (shiftStart.isAfter(now)) {
                // Future shift
                if (shiftDate.isSame(now, 'day')) {
                    status = "Today";
                } else if (shiftDate.isSame(now.clone().add(1, 'day'), 'day')) {
                    status = "Tomorrow";
                } else {
                    status = "Scheduled";
                }
            }

            return {
                ...shift,
                id: shift._id,
                employeeName: employeeName,
                displayDate: shiftDate.format("DD/MM/YYYY"),
                displayStartTime: shiftStart.format("HH:mm"),
                displayEndTime: shiftEnd.format("HH:mm"),
                status: status,
                duration: moment.duration(shiftEnd.diff(shiftStart)).asHours().toFixed(1) + 'h',
                hasClocking: !!clocking,
            };
        }).filter(shift => {
            if (selectedFilter === "All") return true;
            const shiftDate = moment(shift.date);
            const now = moment();
            
            switch (selectedFilter) {
                case "Today":
                    return shiftDate.isSame(now, 'day');
                case "This Week":
                    return shiftDate.isSame(now, 'week');
                case "Upcoming":
                    return shiftDate.isAfter(now, 'day');
                default:
                    return true;
            }
        });
    }, [shiftsResponse, employeesResponse, clockingsResponse, selectedFilter, isLoadingShifts, isLoadingEmployees, isLoadingClockings]);

    const columns = [
        {
            field: "profilePic",
            headerName: "",
            width: 80,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="50%"
                    width="50px"
                    height="50px"
                        backgroundColor={theme.palette.primary.light}
                    sx={{ml: 'auto', mr: 'auto'}}
                    >
                    <Typography fontWeight="bold" variant='h6' color={theme.palette.secondary[300]}>
                        {params.row.employeeName.split(" ").map(n => n[0]).join("").toUpperCase()}
                        </Typography>
                    </Box>
            ),
        },
        { field: "employeeName", headerName: "Employee", flex: 1, cellClassName: "name-column--cell" },
        { field: "displayDate", headerName: "Date", flex: 1 },
        { field: "displayStartTime", headerName: "Start Time", flex: 0.8 },
        { field: "displayEndTime", headerName: "End Time", flex: 0.8 },
        { field: "duration", headerName: "Duration", flex: 0.8 },
        { field: "breakDuration", headerName: "Break (min)", flex: 0.8 },
        { 
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => (
                <Typography 
                    color={
                        params.row.status === 'In Progress' ? theme.palette.success.main :
                        params.row.status === 'Waiting' ? theme.palette.warning.main :
                        params.row.status === 'Absent' ? theme.palette.error.main :
                        params.row.status === 'Completed' ? theme.palette.success.dark :
                        params.row.status === 'Today' ? theme.palette.warning.main :
                        params.row.status === 'Tomorrow' ? theme.palette.info.main :
                        theme.palette.text.primary
                    }
                    fontWeight="bold"
                >
                        {params.row.status} 
                    </Typography>
            )
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.4,
            renderCell: (params) => {
                const isMenuOpen = Boolean(anchorEl) && selectedRow && selectedRow._id === params.row._id;
                return (
                    <Box>
                        <IconButton onClick={(e) => handleMenuClick(e, params)}>
                            <MoreVertIcon />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={isMenuOpen}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            <MenuItem onClick={handleViewOpen}>View</MenuItem>
                            <MenuItem onClick={handleEditOpen}>Edit</MenuItem>
                            <MenuItem onClick={handleDeleteOpen}>Delete</MenuItem>
                        </Menu>
                    </Box>
                );
            },
        }
    ];

    return (
            <Box>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    m="1.5rem 2.5rem"
                >
                <Typography variant="h4" color={theme.palette.secondary[100]}>
                    Shifts
                </Typography>
                
                <Button
                    onClick={handleAddDialogOpen}
                    sx={{
                        backgroundColor: theme.palette.secondary.main,
                        color: theme.palette.background.alt,
                        fontWeight: 'bold',
                        padding: '10px 20px',
                        '&:hover': {
                            backgroundColor: theme.palette.secondary.dark
                        }
                    }}
                >
                    Schedule Shift
                </Button>
            </Box>

            {/* Filter Buttons */}
            <Box display="flex" gap="10px" m="1.5rem 2.5rem">
                {filterOptions.map((option) => (
                    <Button
                        key={option}
                        variant={selectedFilter === option ? "contained" : "outlined"}
                        onClick={() => setSelectedFilter(option)}
                        sx={{
                            backgroundColor: selectedFilter === option ? theme.palette.secondary.light : "transparent",
                            color: selectedFilter === option ? theme.palette.background.alt : theme.palette.secondary[100],
                            borderColor: theme.palette.secondary[100],
                        }}
                    >
                        {option}
                    </Button>
                ))}
            </Box>

            {/* Shifts Data Grid */}
            <Box
                m="1.5rem 2.5rem"
                height="calc(76vh - 40px)"
                    sx={{
                        "& .MuiDataGrid-root": { border: "none", backgroundColor: "transparent" },
                        "& .MuiDataGrid-row": {
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            borderRadius: "1px", cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: theme.palette.secondary.light
                            }
                        },
                        "& .MuiDataGrid-cell": { borderBottom: "none" },
                        "& .name-column--cell": { color: theme.palette.secondary[200], fontWeight: "bold" },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: theme.palette.primary.light,
                            borderBottom: `1px solid ${theme.palette.secondary[300]}`,
                            color: theme.palette.secondary[100],
                        },
                        "& .MuiDataGrid-virtualScroller": { backgroundColor: theme.palette.background.alt },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: `1px solid ${theme.palette.secondary[300]}`,
                            backgroundColor: theme.palette.primary.light,
                            color: theme.palette.secondary[100],
                        },
                        "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${theme.palette.secondary[200]} !important`, fontSize: "16px" },
                    }}
                >
                {(isLoadingShifts || isLoadingEmployees) ? (
                    <Typography sx={{textAlign: 'center', mt: 5}}>Loading shifts data...</Typography>
                ) : (isErrorShifts || isErrorEmployees) ? (
                    <Typography sx={{textAlign: 'center', mt: 5, color: 'red'}}>Error: Could not load data.</Typography>
                ) : (processedData && processedData.length > 0) ? (
                    <DataGrid
                        loading={isLoadingShifts || isLoadingEmployees}
                        rows={processedData || []}
                        columns={columns}
                        slots={{ toolbar: GridToolbar }}
                        rowHeight={isMobileScreen ? 80 : 100}
                    />
                ) : (
                    <Typography sx={{textAlign: 'center', mt: 5}}>
                        {selectedFilter === "All" 
                            ? "No shifts scheduled." 
                            : `No shifts found for "${selectedFilter}".`
                        }
                    </Typography>
                )}
            </Box>

            {/* Delete Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this shift for {selectedRow?.employeeName}? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.error.main,
                        }}
                    >
                        Delete
                    </Button>
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.secondary.main,
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Dialog */}
            <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>Shift Details</DialogTitle>
                <DialogContent>
                    {selectedRow && (
                        <Box>
                            <Typography><strong>Employee:</strong> {selectedRow.employeeName}</Typography>
                            <Typography><strong>Date:</strong> {selectedRow.displayDate}</Typography>
                            <Typography><strong>Start Time:</strong> {selectedRow.displayStartTime}</Typography>
                            <Typography><strong>End Time:</strong> {selectedRow.displayEndTime}</Typography>
                            <Typography><strong>Duration:</strong> {selectedRow.duration}</Typography>
                            <Typography><strong>Break Duration:</strong> {selectedRow.breakDuration} minutes</Typography>
                            <Typography><strong>Status:</strong> {selectedRow.status}</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenViewDialog(false)}
                            variant="contained"
                            color="secondary"
                            sx={{
                                backgroundColor: theme.palette.secondary.main,
                            }}
                        >
                            Close
                        </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit Shift</DialogTitle>
                <DialogContent>
                    {validationAlert.show && (
                        <Alert severity={validationAlert.severity} sx={{ mb: 2 }}>
                            {validationAlert.message}
                        </Alert>
                    )}
                    <Typography sx={{ mb: 2 }}><strong>Employee:</strong> {editableShiftData?.employeeName}</Typography>
                    <TextField
                        margin="dense"
                        label="Date"
                        name="date"
                        type="date"
                        value={editableShiftData?.date || ''}
                        onChange={handleInputChange}
                        fullWidth
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        margin="dense"
                        label="Start Time"
                        name="startTime"
                        type="time"
                        value={editableShiftData?.startTime || ''}
                        onChange={handleInputChange}
                        fullWidth
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        margin="dense"
                        label="End Time"
                        name="endTime"
                        type="time"
                        value={editableShiftData?.endTime || ''}
                        onChange={handleInputChange}
                        fullWidth
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        margin="dense"
                        label="Break Duration (minutes)"
                        name="breakDuration"
                        type="number"
                        value={editableShiftData?.breakDuration || 0}
                        onChange={handleInputChange}
                        fullWidth
                        variant="standard"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenEditDialog(false)}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.error.main,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEditSubmit}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.secondary.main,
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Shift Dialog */}
            <Dialog open={openAddDialog} onClose={handleAddDialogClose} fullWidth maxWidth="sm">
                <DialogTitle>Schedule New Shift</DialogTitle>
                <DialogContent>
                    {validationAlert.show && (
                        <Alert severity={validationAlert.severity} sx={{ mb: 2 }}>
                            {validationAlert.message}
                        </Alert>
                    )}
                    <FormControl fullWidth margin="dense" variant="standard">
                        <InputLabel>Employee*</InputLabel>
                        <Select
                            name="employeeId"
                            value={newShiftData.employeeId}
                            onChange={handleNewShiftInputChange}
                            label="Employee"
                        >
                            {employeesData?.map((employee) => (
                                <MenuItem key={employee._id} value={employee._id}>
                                    {employee.names.firstName} {employee.names.lastName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        label="Date*"
                        name="date"
                        type="date"
                        value={newShiftData.date}
                        onChange={handleNewShiftInputChange}
                        fullWidth
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        margin="dense"
                        label="Start Time*"
                        name="startTime"
                        type="time"
                        value={newShiftData.startTime}
                        onChange={handleNewShiftInputChange}
                        fullWidth
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        margin="dense"
                        label="End Time*"
                        name="endTime"
                        type="time"
                        value={newShiftData.endTime}
                        onChange={handleNewShiftInputChange}
                        fullWidth
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        margin="dense"
                        label="Break Duration (minutes)"
                        name="breakDuration"
                        type="number"
                        value={newShiftData.breakDuration}
                        onChange={handleNewShiftInputChange}
                        fullWidth
                        variant="standard"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleAddDialogClose}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.error.main,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddSubmit}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.secondary.main,
                        }}
                    >
                        Schedule Shift
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Shifts;
